import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getLocaleFormat, formatLargeNumber } from "../../../utils/numbers";
import { getVar, brightenColor, getRandomHexadecimalColor } from "../../../utils/colors";
import { CHART_TYPES } from "../constants";
const calculateFontSize = _ref => {
  let {
    container,
    width,
    maxSize
  } = _ref;
  if (!container || !(container !== null && container !== void 0 && container.offsetWidth) || width) return null;
  const containerWidth = (container === null || container === void 0 ? void 0 : container.offsetWidth) || width;
  let size = Math.max(Math.floor(containerWidth / 50), 10);
  const innerMaxSize = maxSize || 12;
  if (size > innerMaxSize) size = innerMaxSize;
  return size;
};
const addTransparency = _ref2 => {
  let {
    hexColor,
    opacity = 1
  } = _ref2;
  const red = parseInt(hexColor.substring(1, 3), 16);
  const green = parseInt(hexColor.substring(3, 5), 16);
  const blue = parseInt(hexColor.substring(5, 7), 16);
  return ` rgba(${red}, ${green}, ${blue}, ${opacity})`;
};
const isColorDark = rgbColor => {
  const grayscaleValue = 0.299 * rgbColor[0] + 0.587 * rgbColor[1] + 0.114 * rgbColor[2];
  const threshold = 170; // # of 255

  if (grayscaleValue < threshold) return true;else return false;
};
const getLegendPlugins = _ref3 => {
  let {
    type,
    data,
    theme,
    container
  } = _ref3;
  const onHoverLegend = (event, chartElement) => {
    event.native.target.style.cursor = 'pointer';
  };
  const onLeaveLegend = (event, chartElement) => {
    event.native.target.style.cursor = 'default';
  };
  let legendProps = {};
  if (type === CHART_TYPES.doughnut || type === CHART_TYPES.pie) {
    legendProps = {
      position: 'left',
      align: 'center'
    };
  }
  return {
    legend: _objectSpread({
      onHover: onHoverLegend,
      onLeave: onLeaveLegend,
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 10,
        boxHeight: 10,
        padding: 10,
        font: context => ({
          family: theme.typography.subtitle.fontFamily,
          size: calculateFontSize({
            container
          })
        })
      }
    }, legendProps)
  };
};
const getDefineExtraPluggins = _ref4 => {
  let {
    type,
    t,
    theme,
    container,
    locale
  } = _ref4;
  let plugins = [];
  if (type === CHART_TYPES.doughnut) {
    plugins.push({
      id: 'customDoughnutText',
      afterDraw(chart) {
        let ctx = chart.ctx;
        ctx.save();

        // Calculate the center
        let centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
        let centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
        let sum = chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
        let text = formatLargeNumber({
          number: sum,
          locale
        });
        const fontSize = calculateFontSize({
          container,
          maxSize: 18
        });
        let totalFont = `bold ${fontSize}px ${theme.typography.h6.fontFamily}`;
        ctx.font = totalFont;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = getVar(theme.colors.grey[700]);

        // First text with total
        ctx.fillText(text, centerX, centerY);
        const textFont = `${theme.typography.subtitle.fontSize}px ${theme.typography.subtitle.fontFamily}`;
        ctx.font = textFont;
        ctx.fillStyle = getVar(theme.colors.grey[500]);

        // Second text with label
        ctx.fillText(t('label_total'), centerX, centerY + fontSize + 4);
        ctx.restore();
      }
    });
  }
  return plugins;
};
const getColorPalette = theme => {
  return ['#7BA4FE', '#FFC766', '#FF9E82', '#AEE5F8', '#F8DC63', '#E5A5CE', '#5EEBE5', '#A7E675', '#245DFD', '#FF8C00', '#FF3B30', '#76BDE8', '#E9B200', '#AA5B9C', '#00A8BE', '#4CAD20', '#1234B6', '#B75400', '#B7182A', '#3B71A7', '#A77700', '#792D7A', '#006388', '#237C10'];
};
let previousColors = [];
const changeData = _ref5 => {
  let {
    type,
    data,
    bright,
    alpha,
    theme
  } = _ref5;
  const hasMultipleDatasets = data.datasets.length > 1;
  const colorPalette = getColorPalette(theme);
  const getColor = _ref6 => {
    let {
      datasetIndex,
      isBorder
    } = _ref6;
    return (color, index) => {
      if (index <= colorPalette.length - 1) {
        let colorIndex = index % colorPalette.length;
        if (hasMultipleDatasets) {
          colorIndex = datasetIndex % colorPalette.length;
        }
        let newColor = colorPalette[colorIndex];
        if (type === CHART_TYPES.radar) {
          if (!isBorder) {
            newColor = addTransparency({
              hexColor: newColor,
              opacity: 0.5
            });
          } else {
            newColor = addTransparency({
              hexColor: newColor,
              opacity: 0.6
            });
          }
        }
        return newColor;
      } else {
        const newColor = getRandomHexadecimalColor(previousColors);
        previousColors.push(newColor);
        return brightenColor({
          color: newColor,
          bright,
          alpha
        });
      }
    };
  };
  const newData = _objectSpread(_objectSpread({}, data), {}, {
    datasets: data.datasets.map((dataset, datasetIndex) => {
      var _dataset$borderColor;
      const backgroundColor = dataset.backgroundColor.map(getColor({
        datasetIndex
      }));
      const newDataset = _objectSpread(_objectSpread({}, dataset), {}, {
        backgroundColor,
        borderColor: ((_dataset$borderColor = dataset.borderColor) === null || _dataset$borderColor === void 0 ? void 0 : _dataset$borderColor.map(getColor({
          datasetIndex,
          isBorder: true
        }))) || []
      });
      if (type === CHART_TYPES.radar) {
        newDataset.fill = true;
        newDataset.borderWidth = 3;
        newDataset.pointBackgroundColor = backgroundColor;
        newDataset.pointBorderWidth = 2;
      } else if (type === CHART_TYPES.area) {
        const isLast = datasetIndex === data.datasets.length - 1;
        newDataset.fill = isLast ? 'origin' : datasetIndex + 1;
      }
      return newDataset;
    })
  });
  return newData;
};
const getScales = _ref7 => {
  let {
    type,
    container,
    data
  } = _ref7;
  const xGrid = {
    display: false
  };
  const yGrid = {
    display: true
  };
  if (type === CHART_TYPES.hBar) {
    xGrid.display = true;
    yGrid.display = false;
  }
  let scales = {
    x: {
      grid: xGrid,
      ticks: {
        font: context => {
          const size = calculateFontSize({
            container
          });
          return {
            size: size
          };
        },
        format: {
          notation: 'compact',
          // Use compact notation for large numbers
          compactDisplay: 'short' // Display as short (e.g., 1K, 1M)
        }
      }
    },
    y: {
      grid: yGrid,
      ticks: {
        font: context => {
          const size = calculateFontSize({
            container
          });
          return {
            size: size
          };
        },
        format: {
          notation: 'compact',
          // Use compact notation for large numbers
          compactDisplay: 'short' // Display as short (e.g., 1K, 1M)
        }
      }
    }
  };
  if (type === CHART_TYPES.doughnut || type === CHART_TYPES.pie || type === CHART_TYPES.radar) {
    scales = {
      x: {
        display: false,
        y: {
          display: false
        }
      }
    };
  }
  if (type === CHART_TYPES.radar) {
    scales.r = {
      ticks: {
        display: false
      }
    };
  }
  return scales;
};
const adjustPaddingBasedOnLabel = _ref8 => {
  let {
    data,
    container,
    theme,
    padding
  } = _ref8;
  const dataset = data.datasets[0].data;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = `${calculateFontSize({
    container
  })}px ${theme.typography.subtitle.fontFamily}`;
  let maxWidth = 0;
  dataset.forEach(value => {
    const width = ctx.measureText(value.toLocaleString()).width;
    if (width > maxWidth) {
      maxWidth = width;
    }
  });
  const right = maxWidth + 10;
  return _objectSpread(_objectSpread({}, padding), {}, {
    right
  });
};
export const buildChartConfig = _ref9 => {
  var _finalType;
  let {
    type,
    data,
    options = {},
    theme,
    t,
    isWidget,
    container,
    locale,
    onClick
  } = _ref9;
  let customOptions = {};
  let anchor = 'end';
  let offset = -4;
  let align = 'top';
  let padding = {
    top: 0,
    right: 6,
    bottom: 0,
    left: 6
  };
  let elements = {
    bar: {
      borderRadius: 4
    }
  };
  if (type === CHART_TYPES.hBar) {
    customOptions = {
      indexAxis: 'y'
    };
    anchor = 'end';
    align = 'end';
    offset = 4;
    padding = adjustPaddingBasedOnLabel({
      data,
      container,
      theme,
      padding
    });
  } else if (type === CHART_TYPES.doughnut || type === CHART_TYPES.pie) {
    anchor = 'center';
    offset = 0;
    align = 'center';
  }
  let baseOptions = _objectSpread(_objectSpread(_objectSpread({}, options), customOptions), {}, {
    responsive: true,
    animation: {
      duration: 0
    },
    maintainAspectRatio: false,
    devicePixelRatio: window.devicePixelRatio || 1,
    layout: {
      padding
    },
    elements,
    onClick: (event, chartElement) => {
      if (chartElement.length > 0) {
        const element = chartElement[0];
        const datasetIndex = element.datasetIndex;
        const index = element.index;
        const label = data.labels[index];
        const value = data.datasets[datasetIndex].data[index];
        onClick && onClick({
          element,
          index,
          label,
          value
        });
      }
    },
    onHover: (event, elements) => {
      if (!onClick) return;
      const canvas = event.native.target;
      if (elements.length > 0) {
        canvas.style.cursor = 'pointer';
      } else {
        canvas.style.cursor = 'default';
      }
    },
    scales: getScales({
      type,
      container,
      data
    }),
    plugins: _objectSpread({
      customCanvasBackgroundColor: {
        color: getVar(theme.colors.primary.white)
      },
      subtitle: {
        text: data === null || data === void 0 ? void 0 : data.subtitle,
        display: (data === null || data === void 0 ? void 0 : data.subtitle) || false,
        font: {
          size: calculateFontSize({
            container
          })
        }
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: function (tooltipItem) {
            const label = tooltipItem.label;
            return label;
          },
          label: function (tooltipItem) {
            var _tooltipItem$dataset$, _tooltipItem$dataset;
            let value = (_tooltipItem$dataset$ = tooltipItem.dataset.data) === null || _tooltipItem$dataset$ === void 0 ? void 0 : _tooltipItem$dataset$[tooltipItem.dataIndex];
            if (isNaN(value)) return '';
            value = getLocaleFormat(value, locale, {
              minimumFractionDigits: 0,
              maximumFfractionDigits: 2
            });
            const label = (tooltipItem === null || tooltipItem === void 0 ? void 0 : (_tooltipItem$dataset = tooltipItem.dataset) === null || _tooltipItem$dataset === void 0 ? void 0 : _tooltipItem$dataset.label) || '';
            if (label) return `${label}: ${value}`;
            return value;
          }
        },
        boxPadding: 4
      },
      datalabels: {
        color: context => {
          var _context$dataset;
          const backgroundColors = (context === null || context === void 0 ? void 0 : (_context$dataset = context.dataset) === null || _context$dataset === void 0 ? void 0 : _context$dataset.backgroundColor) || [];
          const backgroundColor = backgroundColors === null || backgroundColors === void 0 ? void 0 : backgroundColors[context.dataIndex];
          const isChartPie = type === CHART_TYPES.pie || type === CHART_TYPES.doughnut || false;
          if (!(backgroundColor !== null && backgroundColor !== void 0 && backgroundColor.length) || !isChartPie) return getVar(theme.colors.grey[700]);
          const darks = backgroundColors.filter(current => {
            const rgbString = current;
            let rgbArray = rgbString.match(/\d+/g);
            if (!rgbArray) return false;
            rgbArray = rgbArray.map(function (value) {
              return parseInt(value, 10);
            });
            return isColorDark(rgbArray);
          });
          return darks.length >= Math.ceil((backgroundColors === null || backgroundColors === void 0 ? void 0 : backgroundColors.length) / 2) ? getVar(theme.colors.primary.white) : getVar(theme.colors.grey[700]);
        },
        font: context => {
          const size = calculateFontSize({
            container
          });
          return {
            family: theme.typography.subtitle.fontFamily,
            size,
            weight: 'bold'
          };
        },
        display: context => {
          const value = context.dataset.data[context.dataIndex];
          return value !== 0;
        },
        anchor,
        offset,
        align,
        clip: false,
        formatter: value => {
          if (isNaN(value)) return value;
          return getLocaleFormat(value, locale, {
            minimumFractionDigits: 0,
            maximumFfractionDigits: 2
          });
        },
        textAlign: 'center'
      }
    }, getLegendPlugins({
      type,
      data,
      theme,
      container
    }))
  });
  let finalType = '';
  switch (type) {
    case CHART_TYPES.hBar:
      finalType = CHART_TYPES.bar;
      break;
    case CHART_TYPES.area:
      finalType = CHART_TYPES.line;
      break;
    case CHART_TYPES.bar:
    default:
      finalType = type;
  }
  const getShouldDisplayLegend = _ref0 => {
    var _chart$chartArea, _chart$legend;
    let {
      chart
    } = _ref0;
    if (!(chart !== null && chart !== void 0 && (_chart$chartArea = chart.chartArea) !== null && _chart$chartArea !== void 0 && _chart$chartArea.width) || !(chart !== null && chart !== void 0 && (_chart$legend = chart.legend) !== null && _chart$legend !== void 0 && _chart$legend.legendItems)) return false;
    const chartWidth = chart.chartArea.width;
    const legendItems = chart.legend.legendItems;
    const minChartWidth = 125;
    const maxLegendItems = 16;
    if (chartWidth < minChartWidth || legendItems >= maxLegendItems) return false;
    return true;
  };
  const finalChartConfig = {
    type: (_finalType = finalType) === null || _finalType === void 0 ? void 0 : _finalType.toLowerCase(),
    data: changeData({
      type,
      data,
      bright: 10,
      alpha: 1,
      theme
    }),
    options: _objectSpread({}, baseOptions),
    plugins: [ChartDataLabels, {
      id: 'customCanvasBackgroundColor',
      beforeDraw: (chart, args, options) => {
        const {
          ctx
        } = chart;
        ctx.save();
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = options.color || getVar(theme.colors.primary.white);
        ctx.fillRect(0, 0, chart.width, chart.height);
        ctx.restore();
      }
    }, {
      id: 'legendMargin',
      beforeInit: chart => {
        const originalFit = chart.legend.fit;
        chart.legend.fit = function fit() {
          originalFit.bind(chart.legend)();
          this.height += 16;
        };
      }
    }, {
      id: 'toggleLegendPlugin',
      beforeRender(chart) {
        if (type !== CHART_TYPES.doughnut && type !== CHART_TYPES.pie) return;
        chart.options.plugins.legend.display = getShouldDisplayLegend({
          chart
        });
      },
      resize(chart) {
        if (type !== CHART_TYPES.doughnut && type !== CHART_TYPES.pie) return;
        chart.options.plugins.legend.display = getShouldDisplayLegend({
          chart
        });
        chart.update();
      }
    }, ...getDefineExtraPluggins({
      type,
      t,
      theme,
      container,
      locale
    })]
  };
  return finalChartConfig;
};